import AddIcon from '@mui/icons-material/Add'
import ArchiveIcon from '@mui/icons-material/Archive'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import UnarchiveIcon from '@mui/icons-material/Unarchive'
import {
  alpha,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import { GridColDef, GridDensity, GridRenderCellParams, GridSortModel, useGridApiRef } from '@mui/x-data-grid-premium'
import {
  ServiceMenuItem,
  useCreateServiceMenuItem,
  useDeleteServiceMenuItem,
  useDeprecateServiceMenuItem,
  useServiceMenu,
  useUpdateServiceMenuItem,
} from 'api/aws/service-menu/useServiceMenu'
import GlobalDataGrid from 'components/DataGrid/GlobalDataGrid'
import GlobalGridToolbar from 'components/DataGrid/GlobalGridToolbar'
import { getDensityStyles } from 'components/DataGrid/operators/getDensityStyles'
import { useSnackbarContext } from 'context/SnackbarProvider/SnackbarProvider'
import React, { useCallback, useMemo, useState } from 'react'
import { DensityStyleTypes } from 'types'
import {
  CATEGORY_OPTIONS,
  defaultColumnOrder,
  defaultFilterModel,
  defaultVisibilityModel,
  DELIVERY_ROLE_OPTIONS,
  DEPARTMENT_OPTIONS,
  pinnedColumns,
  SERVICE_LINES,
} from './defaultModels'
import RenderStringCell from './RenderStringCell'
import './ServiceMenu.css'
import ServiceMenuDialog from './ServiceMenuDialog'

// ── Filter chips ─────────────────────────────────────────────────────────────

type FilterKey = 'service_line' | 'category_name' | 'status' | 'isDeprecated'

interface ActiveFilters {
  service_line: string[]
  category_name: string[]
  status: string[]
  isDeprecated: string[]
}

const emptyFilters: ActiveFilters = {
  service_line: [],
  category_name: [],
  status: [],
  isDeprecated: [],
}

// ── Column defs ──────────────────────────────────────────────────────────────

const buildColumns = (
  onEdit: (row: ServiceMenuItem) => void,
  onDelete: (row: ServiceMenuItem) => void,
  onToggleDeprecate: (row: ServiceMenuItem) => void,
  densityStyles: DensityStyleTypes,
  onCreate: () => void
): GridColDef[] => [
  {
    field: 'title',
    headerName: 'Service Name',
    minWidth: 300,
    flex: 1,
    renderCell: params => {
      const { row } = params ?? {}
      return (
        <Box
          sx={{ display: 'flex', alignItems: 'center', height: '100%', cursor: 'pointer', pl: 2 }}
          onClick={() => onEdit(row)}
        >
          <RenderStringCell params={params} densityStyles={densityStyles} />
        </Box>
      )
    },
  },
  {
    field: 'sku',
    headerName: 'SKU',
    width: 130,
  },
  {
    field: 'ta_type',
    headerName: 'TA Type',
    width: 150,
    renderCell: params => {
      return params?.value ? <span className="obx-badge obx-badge-category">{params?.value}</span> : null
    },
  },
  {
    field: 'ta_category',
    headerName: 'TA Category',
    width: 150,
    renderCell: params => {
      return params?.value ? <span className="obx-badge obx-badge-category">{params?.value}</span> : null
    },
  },
  {
    field: 'variant',
    headerName: 'Variant',
    width: 130,
  },
  {
    field: 'service_line',
    headerName: 'Service Line',
    width: 130,
    renderCell: ({ value }: GridRenderCellParams) => {
      if (!value) return null
      const cfg = SERVICE_LINES[value] ?? { label: value, color: '#999' }
      return (
        <Box
          component={'span'}
          className="obx-badge obx-badge-service-line"
          style={{ color: cfg.color, '--service_line_bg': alpha(cfg.color, 0.1) } as React.CSSProperties}
        >
          {cfg.label}
        </Box>
      )
    },
  },
  {
    field: 'category_name',
    headerName: 'Category',
    minWidth: 180,
    flex: 0.7,
    renderCell: ({ value }: GridRenderCellParams) => {
      if (!value) return null
      const cfg = CATEGORY_OPTIONS[value] ?? { label: value, color: '#999' }
      return <span className="obx-badge obx-badge-category">{cfg.label}</span>
    },
  },
  {
    field: 'subcategory',
    headerName: 'Department',
    minWidth: 160,
    flex: 0.7,
    renderCell: ({ value }: GridRenderCellParams) => {
      if (!value) return null
      const cfg = DEPARTMENT_OPTIONS[value] ?? { label: value, color: '#999' }
      return <span className="obx-badge obx-badge-category">{cfg?.label}</span>
    },
  },
  {
    field: 'points',
    headerName: 'Points',
    width: 65,
    type: 'number',
    valueGetter: (value: any) => Number(value) || 0,
    renderCell: params => (
      <span
        className="obx-badge obx-badge-points"
        style={{
          display: 'inline-block',
          fontSize: densityStyles.fontSize,
          width: '100%',
          textAlign: 'center',
          justifyContent: 'center',
        }}
      >
        {params?.value}
      </span>
    ),
  },
  {
    field: 'price',
    headerName: 'Price',
    width: 100,
    type: 'number',
    valueGetter: (value: any) => Number(value) || 0,
    valueFormatter: (value: number) =>
      value != null
        ? value.toLocaleString(undefined, { style: 'currency', currency: 'USD', minimumFractionDigits: 0 })
        : '',
  },
  {
    field: 'cost',
    headerName: 'Cost',
    width: 90,
    type: 'number',
    valueGetter: (value: any) => Number(value) || 0,
    valueFormatter: (value: number) =>
      value != null
        ? value.toLocaleString(undefined, { style: 'currency', currency: 'USD', minimumFractionDigits: 0 })
        : '',
  },
  {
    field: 'margin',
    headerName: 'Margin',
    width: 90,
    valueGetter: (value: any) => `${value ?? 0}%`,
  },
  {
    field: 'estimated_hours',
    headerName: 'Est. Hours',
    width: 100,
  },
  {
    field: 'delivery_role',
    headerName: 'Delivery Role',
    minWidth: 150,
    flex: 0.6,
    renderCell: ({ value }: GridRenderCellParams) => {
      if (!value) return null
      const cfg = DELIVERY_ROLE_OPTIONS[value] ?? { label: value, color: '#999' }
      return <span className="obx-badge obx-badge-category">{cfg?.label}</span>
    },
  },
  {
    field: 'ai_enhanced',
    headerName: 'AI',
    width: 60,
    type: 'boolean',
    renderCell: ({ value }: GridRenderCellParams) =>
      value ? <Chip label="AI" size="small" color="secondary" /> : null,
  },
  {
    field: 'status',
    headerName: 'Status',
    width: 120,
    renderCell: ({ row }: GridRenderCellParams) => {
      const deprecated = row.isDeprecated
      const status = deprecated ? 'Deprecated' : row.status || 'Active'
      const variant = deprecated
        ? 'obx-badge-status--deprecated'
        : status === 'Active'
        ? 'obx-badge-status--active'
        : 'obx-badge-status'
      return <span className={`obx-badge ${variant}`}>{status}</span>
    },
  },
  {
    field: 'actions',
    headerName: '',
    renderHeader: () => (
      <Box
        onClick={onCreate}
        sx={{
          cursor: 'pointer',
          display: 'inline-flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <AddIcon fontSize="small" />
        <span className="obx-badge obx-badge-actions">NEW</span>
      </Box>
    ),
    headerClassName: 'actions-header',
    headerAlign: 'center',
    align: 'center',
    width: 85,
    sortable: false,
    filterable: false,
    pinnable: true,
    disableColumnMenu: true,
    renderCell: ({ row }: GridRenderCellParams) => (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, height: '100%' }}>
        <Tooltip title={row.isDeprecated ? 'Activate' : 'Deprecate'}>
          <IconButton size="small" onClick={() => onToggleDeprecate(row)}>
            {row.isDeprecated ? (
              <UnarchiveIcon fontSize="small" sx={{ color: 'text.success' }} />
            ) : (
              <ArchiveIcon fontSize="small" sx={{ color: 'text.revise' }} />
            )}
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete">
          <IconButton size="small" color="error" onClick={() => onDelete(row)}>
            <DeleteOutlineIcon fontSize="small" sx={{ color: 'text.error' }} />
          </IconButton>
        </Tooltip>
      </Box>
    ),
  },
]

// ── Delete Confirmation Dialog ───────────────────────────────────────────────

interface DeleteDialogProps {
  open: boolean
  item: ServiceMenuItem | null
  onClose: () => void
  onConfirm: () => void
  isDeleting: boolean
}

const DeleteDialog = ({ open, item, onClose, onConfirm, isDeleting }: DeleteDialogProps) => (
  <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
    <DialogTitle>Delete Service</DialogTitle>
    <DialogContent>
      <Typography>
        Are you sure you want to delete <strong>{item?.title}</strong>? This action cannot be undone.
      </Typography>
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose}>Cancel</Button>
      <Button variant="contained" color="error" disabled={isDeleting} onClick={onConfirm}>
        {isDeleting ? 'Deleting...' : 'Delete'}
      </Button>
    </DialogActions>
  </Dialog>
)

// ── Main Page ────────────────────────────────────────────────────────────────

const defaultSort: GridSortModel = [{ field: 'title', sort: 'asc' }]

const ServiceMenuPage = () => {
  const theme = useTheme()
  const { showSnackbar } = useSnackbarContext()
  const apiRef = useGridApiRef()
  const [density, setDensity] = useState<GridDensity>(() => {
    const saved = localStorage.getItem('serviceMenuModel') as GridDensity | null
    return saved ?? 'standard'
  })
  const densityStyles = getDensityStyles(density)
  // Data
  const { data: items = [], isLoading } = useServiceMenu()
  const createMutation = useCreateServiceMenuItem()
  const updateMutation = useUpdateServiceMenuItem()
  const deleteMutation = useDeleteServiceMenuItem()
  const deprecateMutation = useDeprecateServiceMenuItem()
  const [filters, setFilters] = useState<ActiveFilters>(emptyFilters)
  const [quickFilter, setQuickFilter] = useState('')
  // Dialog state
  const [editItem, setEditItem] = useState<Partial<ServiceMenuItem> | null>(null)
  const [editOpen, setEditOpen] = useState(false)
  const [deleteItem, setDeleteItem] = useState<ServiceMenuItem | null>(null)
  const [deleteOpen, setDeleteOpen] = useState(false)

  // Filtered rows
  const rows = useMemo(() => {
    return items
      .map(item => ({ ...item, rowId: item.PK + item.SK }))
      .filter(item => {
        if (filters.service_line.length && !filters.service_line.includes(item.service_line)) return false
        if (filters.category_name.length && !filters.category_name.includes(item.category_name)) return false
        if (filters.status.length) {
          const status = item.isDeprecated ? 'Deprecated' : item.status || 'Active'
          if (!filters.status.includes(status)) return false
        }
        if (filters.isDeprecated.length) {
          const dep = item.isDeprecated ? 'true' : 'false'
          if (!filters.isDeprecated.includes(dep)) return false
        }
        return true
      })
  }, [items, filters])

  // Column defs
  const handleCreate = useCallback(() => {
    setEditItem({})
    setEditOpen(true)
  }, [])

  const handleEdit = useCallback((row: ServiceMenuItem) => {
    setEditItem(row)
    setEditOpen(true)
  }, [])

  const handleDelete = useCallback((row: ServiceMenuItem) => {
    setDeleteItem(row)
    setDeleteOpen(true)
  }, [])

  const handleToggleDeprecate = useCallback(
    (row: ServiceMenuItem) => {
      deprecateMutation.mutate(
        { pk: row.PK, sk: row.SK, isDeprecated: !row.isDeprecated },
        {
          onSuccess: () => showSnackbar(`${row.title} ${row.isDeprecated ? 'activated' : 'deprecated'}`, 'success'),
          onError: () => showSnackbar('Failed to update status', 'error'),
        }
      )
    },
    [deprecateMutation, showSnackbar]
  )

  const columns = useMemo(
    () => buildColumns(handleEdit, handleDelete, handleToggleDeprecate, densityStyles, handleCreate),
    [handleEdit, handleDelete, handleToggleDeprecate, densityStyles, handleCreate]
  )

  // Save handler (create or update)
  const handleSave = useCallback(
    (raw: Partial<ServiceMenuItem>) => {
      // Convert ai_enhanced from string select to boolean
      // Sync isDeprecated flag with status field
      const data = {
        ...raw,
        ai_enhanced: raw.ai_enhanced === true || (raw as any).ai_enhanced === 'true',
        isDeprecated: raw.status === 'Deprecated',
      }
      if (data.PK && data.SK) {
        const { PK, SK, ...fields } = data
        updateMutation.mutate(
          { pk: PK, sk: SK, ...fields },
          {
            onSuccess: () => {
              showSnackbar('Service updated', 'success')
              setEditOpen(false)
              setEditItem(null)
            },
            onError: () => showSnackbar('Failed to update service', 'error'),
          }
        )
      } else {
        createMutation.mutate(data, {
          onSuccess: () => {
            showSnackbar('Service created', 'success')
            setEditOpen(false)
            setEditItem(null)
          },
          onError: () => showSnackbar('Failed to create service', 'error'),
        })
      }
    },
    [createMutation, updateMutation, showSnackbar]
  )

  // Delete handler
  const handleConfirmDelete = useCallback(() => {
    if (!deleteItem) return
    deleteMutation.mutate(
      { pk: deleteItem.PK, sk: deleteItem.SK },
      {
        onSuccess: () => {
          showSnackbar(`${deleteItem.title} deleted`, 'success')
          setDeleteOpen(false)
          setDeleteItem(null)
        },
        onError: () => showSnackbar('Failed to delete service', 'error'),
      }
    )
  }, [deleteItem, deleteMutation, showSnackbar])
  const defaultColumnWidths = useMemo(
    () => Object.fromEntries(columns.filter(col => typeof col.width === 'number').map(col => [col.field, col.width])),
    [columns]
  )
  const changeDensity = useCallback((newDensity: GridDensity) => {
    setDensity(newDensity)
    localStorage.setItem('dealsDensityModel', newDensity)
  }, [])
  const handleFilterChange = useCallback((key: FilterKey, values: string[]) => {
    setFilters(prev => ({ ...prev, [key]: values }))
  }, [])

  const activeFilterCount = Object.values(filters).reduce((sum, arr) => sum + (arr.length > 0 ? 1 : 0), 0)

  // ── Toolbar ──────────────────────────────────────────────────────────────

  return (
    <Box className="service-menu-root" sx={{ height: '100%', display: 'flex', flexDirection: 'column', pt: 1 }}>
      <GlobalDataGrid
        apiRef={apiRef}
        rows={rows}
        columns={columns}
        context={'service_menu'}
        toolbarComponent={GlobalGridToolbar}
        onChangeDensity={changeDensity}
        defaultFilterModel={defaultFilterModel}
        defaultColumnOrder={defaultColumnOrder}
        defaultVisibilityModel={defaultVisibilityModel}
        defaultSortModel={defaultSort}
        defaultGridDensity={'compact'}
        defaultColumnWidths={defaultColumnWidths}
        pinnedColumns={pinnedColumns}
        getRowClassName={params => (params.row.isDeprecated ? 'row-deprecated' : '')}
        treeData={false}
        isLoading={isLoading}
        checkboxSelection={false}
        gridSxProps={{
          // Remove all borders from grid root
          '&.MuiDataGrid-root': { border: 'none !important' },
          // Remove row box-shadow dividers (set by theme)
          '& .MuiDataGrid-row': {
            transition: 'all 0.3s ease',
            boxShadow: 'none !important',
            borderRadius: '4px',
            filter: 'grayscale(100%)',
            '&:hover': {
              backgroundColor: 'rgba(255,255,255,0.03) !important',
              transform: 'scale(1.003)',
              filter: 'grayscale(0%)',
            },
          },
          // Remove cell borders + disable hover scale (set by theme)
          '& .MuiDataGrid-cell': {
            border: 'none !important',
            overflow: 'visible !important',
            '& > *': { transform: 'none !important' },
            '&:hover > *': { transform: 'none !important' },
          },
          // Transparent header background (theme sets rgba(0,0,0,0.7))
          '& .MuiDataGrid-columnHeader': {
            backgroundColor: `${theme.palette.background.default} !important`,
            border: 'none !important',
          },
          '& .MuiDataGrid-container--top [role=row]': {
            backgroundColor: 'transparent !important',
            border: 'none !important',
          },
          '& .MuiDataGrid-columnSeparator': {},
          '& .MuiDataGrid-footerContainer': {
            border: 'none !important',
          },
          '& .MuiDataGrid-filler': {
            display: 'none',
          },
          '& .MuiDataGrid-columnHeaderTitleContainer': {
            backgroundColor: 'transparent !important',
            border: 'none !important',
          },
          '& .MuiDataGrid-cell--pinnedRight, & .MuiDataGrid-cell--pinnedLeft': {
            backgroundColor: `${theme.palette.background.pinnedColumms} !important`,
            boxShadow: 'none !important',
            '& > *': {
              transform: 'none !important',
              transition: 'none !important',
            },
            '&:hover > *': {
              transform: 'none !important',
            },
          },
        }}
      />

      <ServiceMenuDialog
        open={editOpen}
        item={editItem}
        onClose={() => {
          setEditOpen(false)
          setEditItem(null)
        }}
        onSave={handleSave}
        isSaving={createMutation.isPending || updateMutation.isPending}
      />

      <DeleteDialog
        open={deleteOpen}
        item={deleteItem}
        onClose={() => {
          setDeleteOpen(false)
          setDeleteItem(null)
        }}
        onConfirm={handleConfirmDelete}
        isDeleting={deleteMutation.isPending}
      />
    </Box>
  )
}

export default ServiceMenuPage
